<template>
  <div class="list-page">
    <div class="detail-banner">
      <div class="container">
        <h1>精选产品</h1>
      </div>
    </div>
    <section class="section">
      <div class="container">
        <span class="detail-back" @click="$goBack()">‹ 返回</span>

        <!-- 一级产品类型（分类展示 + 分页） -->
        <div class="type-panel" v-if="firstTypes.length">
          <div class="type-grid">
            <button
              v-for="t in firstTypes"
              :key="t.id"
              class="type-card"
              :class="{ active: activeFirstId === t.id }"
              @click="selectFirst(t.id)"
            >
              <span class="type-name">{{ t.name }}</span>
              <span class="type-count">{{ typeCount(t.id) }}</span>
            </button>
          </div>
          <!-- 类型分页 -->
          <div class="pager" v-if="typeTotalPages > 1">
            <button class="pg-btn" :disabled="typePage <= 1" @click="goTypePage(typePage - 1)">‹ 上一页</button>
            <button
              v-for="p in typePages"
              :key="p"
              class="pg-num"
              :class="{ active: p === typePage }"
              @click="goTypePage(p)"
            >{{ p }}</button>
            <button class="pg-btn" :disabled="typePage >= typeTotalPages" @click="goTypePage(typePage + 1)">下一页 ›</button>
          </div>
        </div>

        <div class="empty-block" v-else-if="!typeLoading">暂无产品分类</div>
        <div class="empty-block" v-else>加载中…</div>

        <!-- 当前分类下的二级类型筛选（来自类型树） -->
        <template v-if="activeFirstId && subTypes.length">
          <div class="subtabs">
            <button
              class="subtab"
              :class="{ active: activeSecondId === '' }"
              @click="selectSecond('')"
            >全部</button>
            <button
              v-for="s in subTypes"
              :key="s.id"
              class="subtab"
              :class="{ active: activeSecondId === s.id }"
              @click="selectSecond(s.id)"
            >{{ s.name }}</button>
          </div>
        </template>

        <!-- 产品网格（按类型） -->
        <div class="block-title" v-if="activeFirstName">
          <span class="bt-main">{{ activeFirstName }}</span>
          <span class="bt-sub" v-if="activeSecondName"> / {{ activeSecondName }}</span>
          <span class="bt-count">共 {{ displayTotal }} 件</span>
        </div>

        <div class="card-grid" v-if="displayRows.length">
          <div
            v-for="item in displayRows"
            :key="item.id"
            class="card"
            @click="$router.push('/product/' + item.id)"
          >
            <div class="card-cover product-cover">
              <span class="cover-tag" v-if="item.typeName">{{ item.typeName }}</span>
              <img v-if="item.cover" :src="item.cover" :alt="item.name" @error="onImgError" />
              <span v-else class="cover-letter">{{ item.name.slice(0, 1) }}</span>
            </div>
            <div class="card-body">
              <div class="card-title">{{ item.name }}</div>
              <div class="card-desc">{{ item.summary }}</div>
              <div class="card-source" v-if="item.sourcePlace">来源地：{{ item.sourcePlace }}</div>
              <div class="card-detail-btn">查看详情</div>
            </div>
          </div>
        </div>

        <div class="empty-block" v-else-if="productLoading">加载中…</div>
        <div class="empty-block" v-else-if="activeFirstId">该分类暂无产品</div>
        <div class="empty-block" v-else>请选择上方产品分类</div>

        <!-- 产品分页（二级筛选时为客户端过滤，不再分页） -->
        <div class="pager" v-if="productTotalPages > 1 && !activeSecondId">
          <button class="pg-btn" :disabled="productPage <= 1" @click="goProductPage(productPage - 1)">‹ 上一页</button>
          <button
            v-for="p in productPages"
            :key="p"
            class="pg-num"
            :class="{ active: p === productPage }"
            @click="goProductPage(p)"
          >{{ p }}</button>
          <button class="pg-btn" :disabled="productPage >= productTotalPages" @click="goProductPage(productPage + 1)">下一页 ›</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  fetchProductFirstTypes,
  fetchProductTypeTree,
  fetchProductsByType
} from '../api/home'

export default {
  name: 'ProductsList',
  data () {
    return {
      // 一级类型
      firstTypes: [],
      typeTotal: 0,
      typePage: 1,
      typePageSize: 12,
      typeLoading: true,
      activeFirstId: '',
      // 二级类型（来自类型树）
      subTypes: [],
      activeSecondId: '',
      // 产品列表
      productRows: [],
      productTotal: 0,
      productPage: 1,
      productPageSize: 8,
      productLoading: true,
      // 每个一级类型下的产品数量（用于角标，懒加载）
      typeCountMap: {}
    }
  },
  computed: {
    activeFirstName () {
      const t = this.firstTypes.find(x => x.id === this.activeFirstId)
      return t ? t.name : ''
    },
    activeSecondName () {
      if (!this.activeSecondId) return ''
      const s = this.subTypes.find(x => x.id === this.activeSecondId)
      return s ? s.name : ''
    },
    // 二级筛选后的实际展示列表（客户端按 secondTypeId 过滤当前页产品）
    displayRows () {
      if (!this.activeSecondId) return this.productRows
      return this.productRows.filter(p => String(p.secondTypeId) === String(this.activeSecondId))
    },
    // 二级筛选后的展示数量
    displayTotal () {
      return this.displayRows.length
    },
    typeTotalPages () {
      return Math.max(1, Math.ceil(this.typeTotal / this.typePageSize))
    },
    typePages () {
      return this.buildPages(this.typePage, this.typeTotalPages)
    },
    productTotalPages () {
      return Math.max(1, Math.ceil(this.productTotal / this.productPageSize))
    },
    productPages () {
      return this.buildPages(this.productPage, this.productTotalPages)
    }
  },
  created () {
    this.loadFirstTypes()
  },
  methods: {
    buildPages (cur, tp) {
      if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1)
      const set = new Set([1, tp, cur, cur - 1, cur + 1, cur - 2, cur + 2])
      const arr = [...set].filter(p => p >= 1 && p <= tp).sort((a, b) => a - b)
      const out = []
      let prev = 0
      for (const p of arr) {
        if (p - prev > 1) out.push('...')
        out.push(p)
        prev = p
      }
      return out
    },
    async loadFirstTypes () {
      this.typeLoading = true
      try {
        const res = await fetchProductFirstTypes({
          pageNum: this.typePage,
          pageSize: this.typePageSize
        })
        this.firstTypes = res.rows
        this.typeTotal = res.total
        if (!this.activeFirstId && this.firstTypes.length) {
          this.selectFirst(this.firstTypes[0].id)
        }
      } catch (e) {
        this.firstTypes = []
        this.$toast.show({ message: (e && e.message) || '分类加载失败', type: 'error' })
      } finally {
        this.typeLoading = false
      }
    },
    goTypePage (p) {
      if (p < 1 || p > this.typeTotalPages || p === this.typePage) return
      this.typePage = p
      this.loadFirstTypes()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    async selectFirst (id) {
      if (this.activeFirstId === id) return
      this.activeFirstId = id
      this.activeSecondId = ''
      this.subTypes = []
      this.productPage = 1
      // 并行加载类型树与产品列表
      this.loadTree(id)
      this.loadProducts()
    },
    async loadTree (id) {
      try {
        const tree = await fetchProductTypeTree(id)
        this.subTypes = tree.children || []
      } catch (e) {
        this.subTypes = []
      }
    },
    async loadProducts () {
      if (!this.activeFirstId) return
      this.productLoading = true
      try {
        const res = await fetchProductsByType({
          typeId: this.activeFirstId,
          pageNum: this.productPage,
          pageSize: this.productPageSize
        })
        this.productRows = res.rows
        this.productTotal = res.total
      } catch (e) {
        this.productRows = []
        this.productTotal = 0
        this.$toast.show({ message: (e && e.message) || '产品加载失败', type: 'error' })
      } finally {
        this.productLoading = false
      }
    },
    selectSecond (id) {
      if (this.activeSecondId === id) return
      this.activeSecondId = id
    },
    goProductPage (p) {
      if (p < 1 || p > this.productTotalPages || p === this.productPage) return
      this.productPage = p
      this.loadProducts()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    typeCount (id) {
      return this.typeCountMap[id] || ''
    },
    onImgError (e) {
      e.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
/* 类型分类面板 */
.type-panel {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 22px 22px 16px;
  box-shadow: var(--shadow);
  margin-bottom: 26px;
}
.type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 14px;
}
.type-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 18px;
  border: 1px solid var(--border);
  background: var(--blue-soft-2);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s ease;
  text-align: left;
}
.type-card:hover {
  border-color: var(--primary);
  transform: translateY(-3px);
  box-shadow: var(--shadow-hover);
}
.type-card.active {
  background: var(--primary);
  border-color: var(--primary);
}
.type-card.active .type-name,
.type-card.active .type-count {
  color: #fff;
}
.type-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-main);
}
.type-count {
  font-size: 12px;
  font-weight: 700;
  color: var(--primary);
  background: rgba(36, 68, 203, 0.08);
  padding: 2px 9px;
  border-radius: 20px;
}
.type-card.active .type-count {
  background: rgba(255, 255, 255, 0.22);
}

/* 二级类型筛选 */
.subtabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}
.subtab {
  border: 1px solid var(--border);
  background: #fff;
  color: var(--text-sub);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.22s ease;
}
.subtab:hover { border-color: var(--primary); color: var(--primary); }
.subtab.active {
  background: var(--blue-soft);
  border-color: var(--primary);
  color: var(--primary);
}

/* 当前分类标题 */
.block-title {
  margin: 8px 0 22px;
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.bt-main {
  font-size: 19px;
  font-weight: 800;
  color: var(--text-main);
}
.bt-sub {
  font-size: 15px;
  font-weight: 700;
  color: var(--primary);
}
.bt-count {
  margin-left: auto;
  font-size: 13px;
  color: var(--text-sub);
}

/* 分页器 */
.pager {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 30px;
  flex-wrap: wrap;
}
.pg-btn,
.pg-num {
  min-width: 38px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--border);
  background: #fff;
  color: var(--text-main);
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.pg-btn:hover:not(:disabled),
.pg-num:hover {
  border-color: var(--primary);
  color: var(--primary);
}
.pg-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.pg-num.active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
.pager :not(button) {
  color: var(--text-sub);
  padding: 0 4px;
}
.empty-block {
  text-align: center;
  color: var(--text-sub);
  padding: 40px 0;
}
</style>
